import React from "react";
import { calculateDuration } from "./calculateduration";
import { SERVICES } from "../configs/breakingNewsConfig";

interface CENPreviewProps {
  formData: {
    affectedBackends: string[];
    affectedModules: string[];
    affectedBrands: string[];
    affectedMarkets: string[];
    affectedServices: string[];
    affectedVehicles: string[];
    impactedCustomerServices: string[];
    leadIncidentId: string;
    startDisturbance: string;
    endDisturbance: string;
    customerImpactText: string;
    technicalDescription: string;
    additionalInformation: string;
    status: string;
    gmtOffset: string;
    contactEmail?: string;
    slackName?: string;
    slackLink?: string;
    outageType: string;
    rootCause: string;
    fix: string;
  };
}

const basePath = "";

const CENPreview: React.FC<CENPreviewProps> = ({ formData }) => {
  const parseDate = (dateString: string | undefined): string => {
    if (!dateString) return "";
    try {
      const [day, month, yearTime] = dateString.split(".");
      if (!yearTime) return dateString;
      const [year, time] = yearTime.split(" ");
      if (!time) return dateString;
      const [hours, minutes] = time.split(":");
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  const incidentDuration =
    formData.startDisturbance && formData.endDisturbance
      ? calculateDuration(formData.startDisturbance, formData.endDisturbance)
      : "";

  const getGroupedServices = (): Array<{ parent: string; children: string[] }> => {
    if (!formData.affectedServices || formData.affectedServices.length === 0) return [];
    const groupedServices: Map<string, string[]> = new Map();
    SERVICES.forEach((category) => {
      const selectedChildren = category.children.filter((child) =>
        formData.affectedServices.includes(child)
      );
      if (selectedChildren.length > 0) {
        groupedServices.set(category.parent, selectedChildren);
      }
    });
    return Array.from(groupedServices.entries()).map(([parent, children]) => ({
      parent,
      children,
    }));
  };

  const groupedServices = getGroupedServices();

  const getBaseStatus = (status: string) =>
    status.replace(/\s*-\s*Update$/i, "").trim();

  const getStatusColor = (status: string) => {
    switch (getBaseStatus(status)) {
      case "Troubleshooting":
        return "rgb(255, 64, 34)";
      case "Fix Implementation":
        return "#ffffff";
      case "Resolved":
        return "#1eef97";
      case "Change":
        return "#d3d3d3";
      default:
        return "#ffffff";
    }
  };

  const renderStatus = (uppercase = false) => {
    const hasUpdateSuffix = /\s*-\s*Update$/i.test(formData.status);
    const baseStatusRaw = getBaseStatus(formData.status);
    const baseStatusText = uppercase ? baseStatusRaw.toUpperCase() : baseStatusRaw;
    const updateText = uppercase ? " - UPDATE" : " - Update";

    return (
      <>
        <span style={{ color: getStatusColor(baseStatusRaw) }}>{baseStatusText}</span>
        {hasUpdateSuffix && <span style={{ color: "#ffcc00" }}>{updateText}</span>}
      </>
    );
  };

  const bgColor = "rgb(97, 69, 125)";

  const labelTdStyle: React.CSSProperties = {
    backgroundColor: bgColor,
    color: "#1eef97",
    padding: "5px 8px",
    textAlign: "left",
    lineHeight: "16px",
    fontSize: "12px",
    verticalAlign: "top",
    whiteSpace: "nowrap",
    borderBottom: "1px solid rgba(255,255,255,0.15)",
  };

  const valueTdStyle: React.CSSProperties = {
    padding: "5px 8px",
    textAlign: "left",
    lineHeight: "16px",
    fontSize: "12px",
    color: "#ffffff",
    wordBreak: "break-word",
    borderBottom: "1px solid rgba(255,255,255,0.15)",
  };

  const renderArrayLines = (arr: string[]) => {
    if (!arr || arr.length === 0) return null;
    return arr.map((item, i) => (
      <React.Fragment key={i}>
        {item}
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const renderMultiline = (text: string) => (
    <span
      dangerouslySetInnerHTML={{ __html: text.replace(/\n/g, "<br />") }}
    />
  );

  interface Row {
    label: string;
    value: React.ReactNode;
  }

  const rows: Row[] = (
    [
      formData.leadIncidentId
        ? {
            label: "Incident Ticket",
            value: formData.leadIncidentId.toLowerCase().startsWith("ir") ? (
              <a
                href={`https://sc3.vwgroup.com/sc3/index.do?ctx=docEngine&file=probsummary&query=number%3D%22${formData.leadIncidentId}%22&action=&title=Incident%20${formData.leadIncidentId}`}
                style={{ color: "#ffffff", textDecoration: "underline" }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {formData.leadIncidentId}
              </a>
            ) : (
              formData.leadIncidentId
            ),
          }
        : null,

      formData.outageType
        ? { label: "Priority", value: formData.outageType }
        : null,

      formData.startDisturbance
        ? {
            label: `Start Time (Berlin Time ${formData.gmtOffset})`,
            value: parseDate(formData.startDisturbance),
          }
        : null,

      formData.endDisturbance
        ? {
            label: `End Time (Berlin Time ${formData.gmtOffset})`,
            value: parseDate(formData.endDisturbance),
          }
        : null,

      formData.endDisturbance && incidentDuration
        ? { label: "Incident Duration (hh:mm)", value: incidentDuration }
        : null,

      formData.customerImpactText && formData.customerImpactText.trim()
        ? {
            label: "Customer Impact",
            value: renderMultiline(formData.customerImpactText),
          }
        : null,

      formData.impactedCustomerServices && formData.impactedCustomerServices.length > 0
        ? {
            label: "Impacted Customer Services",
            value: renderArrayLines(formData.impactedCustomerServices),
          }
        : null,

      formData.affectedBrands && formData.affectedBrands.length > 0
        ? { label: "Brand", value: renderArrayLines(formData.affectedBrands) }
        : null,

      formData.affectedMarkets && formData.affectedMarkets.length > 0
        ? { label: "Market", value: renderArrayLines(formData.affectedMarkets) }
        : null,

      formData.affectedVehicles && formData.affectedVehicles.length > 0
        ? { label: "Generation", value: renderArrayLines(formData.affectedVehicles) }
        : null,

      formData.rootCause && formData.rootCause.trim()
        ? {
            label: "Root Cause",
            value: renderMultiline(formData.rootCause),
          }
        : null,

      formData.fix && formData.fix.trim()
        ? { label: "Fix", value: renderMultiline(formData.fix) }
        : null,

      formData.affectedBackends && formData.affectedBackends.length > 0
        ? {
            label: "Affected Backends",
            value: renderArrayLines(formData.affectedBackends),
          }
        : null,

      formData.affectedModules && formData.affectedModules.length > 0
        ? {
            label: "Affected Modules",
            value: renderArrayLines(formData.affectedModules),
          }
        : null,

      groupedServices.length > 0
        ? {
            label: "Affected Services",
            value: (
              <>
                {groupedServices.map((group, gi) => (
                  <div key={gi}>
                    <strong>{group.parent}:</strong>{" "}
                    {group.children.join(", ")}
                  </div>
                ))}
              </>
            ),
          }
        : null,

      {
        label: "Status",
        value: (
          <span
            style={{
              fontWeight: "bold",
              textTransform: "uppercase",
            }}
          >
            {renderStatus()}
          </span>
        ),
      },

      formData.technicalDescription && formData.technicalDescription.trim()
        ? {
            label: "Technical Description",
            value: renderMultiline(formData.technicalDescription),
          }
        : null,

      formData.additionalInformation && formData.additionalInformation.trim()
        ? {
            label: "Additional Information",
            value: renderMultiline(formData.additionalInformation),
          }
        : null,
    ] as (Row | null)[]
  ).filter((r): r is Row => r !== null);

  const Divider = () => (
    <tr>
      <td style={{ padding: "6px 0" }}>
        <table
          border={0}
          cellPadding={0}
          cellSpacing={0}
          style={{ width: "100%" }}
        >
          <tbody>
            <tr>
              <td
                style={{
                  backgroundColor: "#ffffff",
                  height: "2px",
                  lineHeight: "2px",
                  fontSize: "0px",
                }}
              >
                &nbsp;
              </td>
            </tr>
          </tbody>
        </table>
      </td>
    </tr>
  );

  return (
    <table
      cellPadding="0"
      cellSpacing="0"
      style={{
        width: "100%",
        maxWidth: "650px",
        margin: "0 auto",
        backgroundColor: bgColor,
      }}
    >
      <tbody>
        <tr>
          <td>
            <table
              cellPadding="0"
              cellSpacing="0"
              style={{
                width: "100%",
                maxWidth: "600px",
                backgroundColor: bgColor,
                color: "#ffffff",
                fontFamily: "Arial, sans-serif",
                padding: "8px 0",
                margin: "auto",
                borderSpacing: "0",
              }}
            >
              <tbody>
                {/* Header Image */}
                <tr>
                  <td style={{ paddingBottom: "6px" }}>
                    <img
                      src={`${basePath}/cariadCenHeader.png`}
                      alt="Header"
                      width="100%"
                      style={{
                        maxWidth: "600px",
                        height: "auto",
                        display: "block",
                      }}
                    />
                  </td>
                </tr>

                {/* Title */}
                <tr>
                  <td
                    style={{
                      padding: "0",
                      border: "0",
                      lineHeight: "1",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        color: "#1eef97",
                        fontSize: "16px",
                        fontWeight: "bold",
                        padding: "6px 0",
                      }}
                    >
                      CRITICAL EVENT NOTIFICATION -{" "}
                      <span>
                        {renderStatus(true)}
                      </span>
                    </div>
                  </td>
                </tr>

                {/* Intro Text */}
                <tr>
                  <td
                    style={{
                      fontSize: "12px",
                      paddingBottom: "4px",
                      textAlign: "center",
                      lineHeight: "16px",
                    }}
                  >
                    Dear Ladies and Gentlemen, Currently we are facing an
                    unplanned service disturbance of Connected Car services. We
                    would like to apologize for any inconvenience caused,
                    however we assure that our teams are working on it.
                  </td>
                </tr>

                {/* Divider */}
                <Divider />

                {/* Details Table */}
                <tr>
                  <td style={{ padding: "0 0 8px 0" }}>
                    <table
                      cellPadding={0}
                      cellSpacing={0}
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontFamily: "Arial, sans-serif",
                        fontSize: "12px",
                      }}
                    >
                      <tbody>
                        {rows.map(({ label, value }, idx) => (
                          <tr key={label + idx}>
                            <td style={labelTdStyle}>{label}</td>
                            <td style={valueTdStyle}>{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>

                {/* Divider */}
                <Divider />

                {/* Preliminary Note */}
                <tr>
                  <td
                    style={{
                      padding: "0",
                      border: "0",
                      lineHeight: "1",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "12px", padding: "8px 0" }}>
                      Please note that the information is preliminary and is
                      prone to changes as we progress with the analysis.
                    </div>
                  </td>
                </tr>

                {/* Links Row */}
                <tr>
                  <td
                    style={{
                      fontSize: "12px",
                      padding: "0 0 8px 0",
                      textAlign: "center",
                      lineHeight: "16px",
                    }}
                  >
                      <a
                          href="https://app.powerbi.com/groups/me/apps/ab2414cc-ac80-41f0-a481-ff2d7b80e351/reports/e79e0536-a26e-40a7-ac43-68715d55b7fd/140ba4270b1346b353cc?experience=power-bi"
                          style={{ color: "#1eef97", textDecoration: "none" }}
                          target="_blank"
                          rel="noopener noreferrer"
                      >
                          Link to Report
                      </a>
                      &nbsp;&nbsp;
                    <a
                      href="https://announcement-tool.care.cariad.digital/subscription"
                      style={{ color: "#1eef97", textDecoration: "none" }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Subscribe
                    </a>
                    &nbsp;&nbsp;
                    <a
                      href="https://announcement-tool.care.cariad.digital/subscription"
                      style={{ color: "#1eef97", textDecoration: "none" }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Unsubscribe
                    </a>
                    &nbsp;&nbsp;
                    <a
                      href="https://dice.cariad.digital/incidents"
                      style={{ color: "#1eef97", textDecoration: "none" }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      DiCE
                    </a>
                    &nbsp;&nbsp;
                  </td>
                </tr>

                {/* Footer Image */}
                <tr>
                  <td style={{ paddingBottom: "8px" }}>
                    <img
                      src={`${basePath}/cariadCenFooter.png`}
                      alt="Footer"
                      width="100%"
                      style={{
                        maxWidth: "600px",
                        height: "auto",
                        display: "block",
                      }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default CENPreview;

